// CRM area — leads/inquiries, customers (detail panels), unified inbox.
export const crm = {
  fields: {
    name: "Name",
    firstName: "First name",
    lastName: "Last name",
    street: "Street",
    city: "City",
    postalCode: "Postal code",
    country: "Country",
    birthDate: "Date of birth",
    nationality: "Nationality",
    passportNumber: "Passport number",
    passportExpiry: "Passport expiry",
  },
  leads: {
    title: "Inquiries",
    subtitle: "Sales funnel — leads before they become orders",
    newLead: "New lead",
    kanban: "Kanban",
    searchPlaceholder: "Search by name, email, phone, or number…",
    agent: "Agent",
    unassigned: "Unassigned",
    kindFacet: "Type",
    kinds: {
      inquiry: "Inquiry",
      callback: "Callback request",
    },
    noLeads: "No leads found",
    table: {
      lead: "Lead",
      contact: "Contact",
      interest: "Interest",
      pax: "Pax",
      value: "Value",
      agent: "Agent",
    },
  },
  leadKanban: {
    title: "Inquiries Kanban",
    subtitle: "Drag cards to move leads through the funnel",
    tableView: "Table",
    loadError: "Error loading leads.",
    lostCount_one: "{{count}} lost lead (not shown in kanban)",
    lostCount_few: "{{count}} lost leads (not shown in kanban)",
    lostCount_other: "{{count}} lost leads (not shown in kanban)",
    generalInquiry: "General inquiry",
    noLeads: "No leads",
  },
  leadDetail: {
    createdAt: "Created {{date}}",
    deleteConfirm:
      "Delete lead {{number}}? Linked messages and orders keep their history — only the lead record is removed.",
    deleteFailed: "Could not delete the lead",
    buildOffer: "Build offer",
    contactSection: "Contact",
    assignedAgent: "Assigned agent",
    unassignedOption: "— Unassigned —",
    interestSection: "Interest",
    noneCustomOption: "— None / custom —",
    noneOption: "— None —",
    requestedDate: "Requested date",
    estimatedValue: "Estimated value ({{currency}})",
    adults: "Adults",
    children: "Children",
    pipelineSection: "Pipeline",
    nextAction: "Next action (callback)",
    lostReason: "Lost reason",
    customerNotes: "Customer notes",
    internalNotes: "Internal notes",
    conversionSection: "Conversion",
    viewOrder: "View order",
    createOrder: "Create order",
    leadScore: "Lead score",
    tripProfile: "Trip profile",
    hotel: {
      title: "Hotel stay (from the inquiry)",
      stay: "Stay",
      nights_one: "{{count}} night",
      nights_few: "{{count}} nights",
      nights_other: "{{count}} nights",
      room: "Room",
      meal: "Board",
      departure: "Departure from",
      included: "included",
    },
    custom: {
      title: "Custom trip request",
      where: "Where",
      whereUnknown: "Not decided (customer doesn't know yet)",
      window: "Travel window",
      windowFrom: "from {{date}}",
      windowTo: "until {{date}}",
      duration_one: "{{count}} day",
      duration_few: "{{count}} days",
      duration_other: "{{count}} days",
      durationLabel: "Length",
      tripType: "Trip type",
      originPath: "Came from",
    },
    sources: {
      web_form: "Website — trip inquiry",
      web_custom: "Website — custom trip",
      contact_form: "Website — contact form",
      email: "E-mail",
      phone: "Phone",
      manual: "Entered manually",
      web_callback: "Website — callback request",
    },
    departure: {
      title: "Date from the inquiry",
      departure: "Departure",
      lifecycle: "Departure status",
      notListed: "Not a listed departure",
      period: "Requested period",
      flexibility: "Flexibility",
    },
    childrenAges: "Children: {{ages}}",
    autoRoutedGarant: "Auto-routed via destination garant",
    autoRoutedRoundRobin: "Auto-routed via round-robin",
    attributionSection: "Attribution",
    source: "Source",
    campaign: "Campaign",
    medium: "Medium",
    clickId: "Click ID",
    aiSuggestedReply: "AI-suggested first reply",
    useAsReply: "Use as reply",
    noContactEmail: "No contact email on this lead",
    profile: {
      fit: "Tailor-made",
      guided: "Guided tour",
      stay: "Resort stay",
      service: "Service",
      economy: "Economy",
      premium: "Premium eco",
      business: "Business",
      fixedDates: "Fixed dates",
      plusMinusWeek: "± a week",
      flexible: "Flexible",
      firstAsia: "First long-haul trip",
      experienced: "Long-haul experienced",
      expert: "Seasoned traveller",
    },
  },
  customers: {
    title: "Customers",
    subtitle: "Overview of all customers",
    newCustomer: "New customer",
    totalCustomers: "Total customers",
    newsletterSubscribers: "Newsletter subscribers",
    newThisMonth: "New this month",
    searchPlaceholder: "Search by name, email or phone…",
    source: "Source",
    noCustomers: "No customers found",
    newsletterBadge: "Newsletter",
    table: {
      customer: "Customer",
      contact: "Contact",
      address: "Address",
      registered: "Registered",
      trips: "Trips",
      spent: "Spent",
    },
  },
  newCustomer: {
    subtitle: "Manually add a customer to the database",
    basicInfo: "Basic information",
    address: "Address",
    personalDetails: "Personal details",
    notesPreferences: "Notes and preferences",
    notesPlaceholder: "Internal notes about the customer…",
    subscribeNewsletter: "Subscribe to newsletter",
    createCustomer: "Create customer",
    createFailed: "Failed to create customer",
    fillRequired: "Please fill in first name, last name and email",
    phonePlaceholder: "+1 234 567 8900",
    nationalityPlaceholder: "American",
    countries: {
      us: "United States",
      gb: "United Kingdom",
      au: "Australia",
      ca: "Canada",
      vn: "Vietnam",
      other: "Other",
    },
  },
  customerDetail: {
    notFound: "Customer not found or an error occurred.",
    deleteConfirm: "Are you sure you want to delete this customer? This action cannot be undone.",
    deleteFailed: "Could not delete the customer",
    messageSent: "Message sent",
    sendFailed: "Failed to send",
    generatingContract: "Generating contract…",
    generatingInvoice: "Generating invoice…",
    contractDownloaded: "Contract downloaded",
    invoiceDownloaded: "Invoice downloaded",
    downloadError: "Download error",
    emailsSection: "Emails",
    documentsSection: "Documents",
    sendEmail: "Send email",
    sendConfirmation: "Send confirmation",
    sendContract: "Send contract",
    sendInvoice: "Send invoice",
  },
  customerInfo: {
    title: "Customer information",
    newsletterSubscription: "Newsletter subscription",
    registered: "Registered: {{date}}",
    validUntil: "(valid until {{date}})",
    newsletterActive: "Newsletter active",
    newsletterInactive: "Newsletter inactive",
    source: "Source: {{source}}",
  },
  customerStats: {
    totalSpent: "Total spent",
    tripsCount: "Number of trips",
    orders: "Orders",
    score: "Score",
  },
  customerOrders: {
    title: "Order history",
    newOrder: "New order",
    unknownTrip: "Unknown trip",
    createdAt: "Created: {{date}}",
    contract: "Contract",
    invoice: "Invoice",
    downloadContract: "Download contract",
    downloadInvoice: "Download invoice",
    email: "Email",
    empty: "No orders",
    timeline: "Timeline",
    firstTrip: "First trip: {{date}}",
    lastTrip: "Last trip: {{date}}",
  },
  communications: {
    title: "Communications",
    newMessage: "New message",
    sent: "Sent",
    received: "Received",
    delivered: "Delivered",
    opened: "Opened",
    from: "From: {{name}}",
    empty: "No communications",
    types: {
      email: "Email",
      phone: "Phone",
      sms: "SMS",
      note: "Note",
    },
  },
  gdpr: {
    title: "GDPR",
    anonymized: "Anonymized",
    description:
      "Export every personal record we hold (portability), or erase the customer's personal data while keeping the financial records (right to be forgotten).",
    exportJson: "Export JSON",
    exportCsv: "Export CSV",
    exportFailed: "Export failed",
    anonymize: "Anonymize (erase)",
    anonymizing: "Anonymizing…",
    anonymizeConfirm:
      "Permanently anonymize this customer's personal data? Orders and invoices are kept (anonymized). This cannot be undone.",
    anonymizeFailed: "Anonymize failed",
    adminOnly: "Only an admin can erase a customer.",
  },
  interestProfile: {
    title: "Interest profile",
    updated: "updated {{date}}",
    stats: {
      tripViews: "Trip views",
      readingMin: "Reading min",
      inquiries: "Inquiries",
      orders: "Orders",
      nlOpens: "NL opens",
      nlClicks: "NL clicks",
    },
    destinationsDerived: "Destinations (derived from behaviour)",
    tripStyleDerived: "Trip style (derived)",
    weight: "weight {{value}}",
    chosenByContact: "Chosen by the contact (preference center)",
    typeChip: "type: {{type}}",
    monthlyDigest: "monthly digest only",
    everyEdition: "every edition",
    pausedUntil: "paused until {{date}}",
    lastEngaged: "Last engaged {{date}}",
    bounces_one: "{{count}} bounce",
    bounces_few: "{{count}} bounces",
    bounces_other: "{{count}} bounces",
  },
  newsletters: {
    title: "Newsletters",
    wave: "wave {{wave}}",
    variant: "variant {{variant}}",
    clicked: "clicked",
    opened: "opened",
    viewEmail: "View email",
    badRecTitle: "Bad recommendation — tell the engine why",
    badRec: "Bad recommendation",
    recommended: "Recommended: {{titles}}",
    flagPlaceholder: "Why was this wrong for them? (e.g. 'already did northern Vietnam twice')",
    flagIt: "Flag it",
    flagged: "Flagged — the analyst will learn from this",
    reconstructionTitle: "Sent email reconstruction",
    sentEmail: "Sent email",
    reconstructionFailed: "Reconstruction failed",
    loadFailed: "Failed to load email",
  },
  sendModal: {
    titleEmail: "Send email",
    titleDocument: "Send document",
    recipient: "Recipient:",
    orderOptional: "Order (optional)",
    withoutOrder: "Without order",
    template: "Template",
    selectTemplate: "Select a template",
    variablesHint:
      "Variables like customer name, order number, etc. will be automatically replaced.",
    generateAndSend: "Generate and send",
  },
  inbox: {
    title: "Inbox",
    subtitle: "Unified messages — Gmail, social DMs, phone",
    syncNow: "Sync now",
    synced_one: "Synced — {{count}} new message",
    synced_few: "Synced — {{count}} new messages",
    synced_other: "Synced — {{count}} new messages",
    syncFailed: "Sync failed",
    mailboxConnected: "Mailbox connected",
    oauthMisconfigured:
      "OAuth misconfigured: this API origin's redirect URI is not registered in the Google Cloud console.",
    mailboxConnectFailed: "Could not connect mailbox",
    searchPlaceholder: "Search messages…",
    channel: "Channel",
    noMessages: "No messages",
    unknownSender: "Unknown",
    toAddress: "To: {{address}}",
    noSubject: "(no subject)",
    aiTriage: "AI triage: {{intent}}",
    createLead: "Create lead",
    channels: {
      email: "Email",
      instagram: "Instagram",
      facebook: "Facebook",
      webchat: "Web chat",
      phone: "Phone",
      webInquiry: "Trip inquiry",
      contactForm: "Contact form",
    },
    intents: {
      sales: "sales",
      question: "question",
      supplier: "supplier",
      noise: "noise",
    },
    settingsTitle: "Inbox settings",
    oauthNotConfigured:
      "Google OAuth client not configured — set GDRIVE_CLIENT_ID / GDRIVE_CLIENT_SECRET.",
    connectedMailboxes: "Connected mailboxes",
    test: "Test",
    testFailed: "Failed",
    disconnect: "Disconnect",
    noMailboxes: "No mailboxes connected yet.",
    connectMailbox: "Connect a mailbox",
    callSummary: "Call summary",
    proposedFollowUp: "Proposed follow-up {{date}}",
    scheduled: "Scheduled",
    followUpScheduled: "Follow-up scheduled",
    followUpFailed: "Failed to apply follow-up",
    attachment: "attachment",
    attachmentN: "Attachment {{n}}",
    parseDocument: "Parse document",
    noParsedFields: "No supplier-document fields found.",
    replyTo: "Reply to",
    draftReply: "Draft reply",
    draftFailed: "Couldn't draft a reply.",
    replyPlaceholder: "Write your reply…",
  },
};
